import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  ThemeProvider,
  Tooltip,
  createTheme,
  useMediaQuery
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import { Theme } from '@mui/material/styles';
import { SIDE_NAV_WIDTH, TOP_NAV_HEIGHT } from './consts';
import { useSelector } from 'react-redux';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Logout } from '@mui/icons-material';
import React from 'react';
import { RootState } from '../../store/store';
import { useNavigate } from 'react-router-dom';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import GavelIcon from '@mui/icons-material/Gavel';
import SchoolIcon from '@mui/icons-material/School';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import { RoleEnum } from '../../types/enums';
import { BannerAd } from './BannerAd';
/**
 * @remarks This is the whole site's top Navigation which
 * gives users a way to navigate to their profile, and also to log out.
 * @param props - properties passed into TopNav when the conponent is called.
 * @returns - a box covering the top of the screen which
 * has a button to log out and view you're own users profile.
 */
export const TopNav = (props) => {
  const { onNavOpen } = props;
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const user = useSelector((state: RootState) => state.userDetail);
  const navigate = useNavigate();

  const onLogout = () => {
    handleProfileMenuClose();
    navigate("/logout");
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: '#484A57'
      },
      secondary: {
        main: '#10AD61'
      },
      background: {
        default: '#484A57'
      },
    },
    typography: {
      h6: {
        color: '#484A57'
      }
    }
  });

  const onViewProfile = () => {
    navigate('/profile');
  }

  const getInitials = () => {
    // return user.name?.split('.').map((n) => n[0]).join('').toUpperCase();
    return user.firstName?.charAt(0).toUpperCase() + user.lastName?.charAt(0).toUpperCase();
  };

  const [profileAnchorEl, setProfileAnchorEl] = React.useState<null | HTMLElement>(null);
  const openProfileMenu = Boolean(profileAnchorEl);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  }

  const getRoleIcon = (roleId: number) => {
    switch (roleId) {
      case RoleEnum.MasterSystemAdmin:
        return <LocalPoliceIcon />
      case RoleEnum.SystemAdministrator:
        return <SecurityIcon />;
      case RoleEnum.CredentialingManager:
        return <ManageAccountsIcon />;
      case RoleEnum.Judge:
        return <GavelIcon />;
      case RoleEnum.SchoolCoordinator:
        return <HistoryEduIcon />;
      case RoleEnum.Youth:
        return <SchoolIcon />;
      default:
        break;
    }
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box
          component="header"
          sx={{
            backdropFilter: 'blur(6px)',
            backgroundColor: '#484A57',
            position: 'fixed',
            left: {
              lg: `${SIDE_NAV_WIDTH}px`
            },
            top: 0,
            width: {
              lg: `calc(100% - ${SIDE_NAV_WIDTH}px)`,
              xs: '100%'
            },
            height: { lg: '90px' },
            zIndex: theme.zIndex.appBar
          }}
        >
          <Stack
            alignItems={user.roleId >= RoleEnum.CredentialingManager ? "center" : "flex-end"}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{
              minHeight: TOP_NAV_HEIGHT,
              px: 2
            }}
          >
            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
            >
              {!lgUp && (
                <IconButton className='color-white' onClick={onNavOpen}>
                  <SvgIcon fontSize="medium">
                    <MenuIcon />
                  </SvgIcon>
                </IconButton>
              )}
            </Stack>
            {user.roleId >= RoleEnum.CredentialingManager && lgUp &&
              <BannerAd />
            }
            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
            >
              <Avatar
                onClick={handleProfileMenuOpen}
                style={{ color: "white", backgroundColor: "#06bd9b" }}
                sx={{
                  cursor: 'pointer',
                  height: 40,
                  width: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  typography: 'body2'
                }}
              >
                {getInitials()}
              </Avatar>
              <Tooltip title="Logout">
                <IconButton style={{ color: "white" }} onClick={onLogout}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Menu
            anchorEl={profileAnchorEl}
            id="account-menu"
            open={openProfileMenu}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem style={{ pointerEvents: 'none' }}>
              <Avatar sx={{ bgcolor: "#06bd9b", color: "#fff" }}  >
                {getInitials()}
              </Avatar> {user?.firstName + " " + user?.lastName}
            </MenuItem>
            <MenuItem style={{ pointerEvents: 'none' }}>
              <Avatar sx={{ bgcolor: "#10AD61", color: "#fff" }}  >
                <EmailIcon />
              </Avatar> {user?.email}
            </MenuItem>
            <MenuItem style={{ pointerEvents: 'none' }}>
              <Avatar sx={{ bgcolor: "#06bd9b", color: "#fff" }}  >
                {getRoleIcon(user?.roleId)}
              </Avatar> {user?.role}
            </MenuItem>
            <Divider />
            <MenuItem onClick={onViewProfile}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={onLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </ThemeProvider>
    </>
  );
};
